import { AuthService } from '@aipb/auth';
import {
  BaseResponse,
  DROPBOX_WRAPPER_API_URL,
  ImageDownloadBatchArg,
  ImageDownloadBatchParams,
  ImageDownloadBatchResponse,
  ImageFormatInfoParams,
  ImageFormatInfoResponse,
  ImageFormatParams,
  ImageQualityInfoParams,
  ImageQualityInfoResponse,
  ImageQualityParams,
  ImageRaw,
  ThumbnailEntry,
  ZipHistoryItem,
  ZipInitResponse,
  ZipProgressResponse,
  ZipStartParams,
  ZipStartResponse
} from '@aipb/dropbox/model';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const parseContentDispositionHeader = (headers: HttpHeaders): string[] => {
  const contentDisposition = headers.get('content-disposition');

  if (!contentDisposition) return [];

  const regex = new RegExp(/.+=(.*)/g);
  const match = regex.exec(contentDisposition);
  // @ts-expect-error foo
  const filename = match[1];
  const s = filename.split('.');

  return [s[0], s[1]];
};

@Injectable({
  providedIn: 'root'
})
export class DropboxWrapperApiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  private readonly _baseUrl: string = inject(DROPBOX_WRAPPER_API_URL);

  constructor() {
    this.handleError = this.handleError.bind(this);
  }

  get baseUrl(): string {
    return this._baseUrl;
  }

  imageDownload(path: string): Observable<ImageRaw> {
    return this.getBlob('image/download', {
      path
    }).pipe(
      map((data) => {
        // @ts-expect-error foo
        return [new Blob([data['body']]), parseContentDispositionHeader(data['headers'])];
      })
    );
  }

  imageDownloadBatch(
    images: ImageDownloadBatchArg[],
    params: ImageDownloadBatchParams
  ): Observable<ImageDownloadBatchResponse[]> {
    return this.post('image/download/batch', {
      entries: <ThumbnailEntry[]>images.map((image) => ({
        path: image.path,
        id: image.id,
        format: 'jpeg',
        ...params
      }))
    });
  }

  imageFormat(params: ImageFormatParams): Observable<ImageRaw> {
    return this.getBlob('image/format', params).pipe(
      map((data) => {
        // @ts-expect-error foo
        return [new Blob([data['body']]), parseContentDispositionHeader(data['headers'])];
      })
    );
  }

  imageFormatInfo(params: ImageFormatInfoParams): Observable<ImageFormatInfoResponse> {
    return this.get<ImageFormatInfoResponse>('image/format/info', params);
  }

  imageQuality(params: ImageQualityParams): Observable<ImageRaw> {
    return this.getBlob('image/quality', params, new HttpHeaders()).pipe(
      map((data) => {
        // @ts-expect-error foo
        return [new Blob([data['body']]), parseContentDispositionHeader(data['headers'])];
      })
    );
  }

  imageQualityInfo(params: ImageQualityInfoParams): Observable<ImageQualityInfoResponse> {
    return this.get<ImageQualityInfoResponse>('image/quality/info', params);
  }

  zipInit(): Observable<ZipInitResponse> {
    return this.get<ZipInitResponse>('zip/init');
  }

  zipStart(params: ZipStartParams): Observable<ZipStartResponse> {
    return this.post<ZipStartResponse>('zip/start', params);
  }

  zipProgress(id: string): Observable<ZipProgressResponse> {
    return this.get<ZipProgressResponse>(`zip/progress/${id}`);
  }

  zipDownload(id: string) {
    return this.getBlob(`zip/download/${id}`);
  }

  zipHistory(): Observable<ZipHistoryItem[]> {
    return this.get<ZipHistoryItem[]>('zip/bundle-history');
  }

  zipHistoryItem(id: string): Observable<ZipHistoryItem> {
    return this.get<ZipHistoryItem>(`zip/bundle-history/${id}`);
  }

  zipRename(id: string, name: string) {
    return this.put<string>(`zip/rename/${id}`, {
      zip_name: name
    });
  }

  zipDelete(id: string) {
    return this.delete<string>(`zip/delete/${id}`);
  }

  zipDownloadUrl(id: string) {
    return `${this._baseUrl}zip/download/${id}`;
  }

  zipHtmlUrl(id: string) {
    return `${this._baseUrl}zip/downloadPage?id=${id}`;
  }

  public post<T>(verb?: string, bodyData: object = {}, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    const token = this.auth.token;
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    return this.http
      .post<BaseResponse<T>>(this._baseUrl + verb, bodyData, {
        headers: headers
      })
      .pipe(
        catchError(this.handleError),
        map((data) => data.response)
      );
  }

  public put<T>(verb?: string, bodyData: object = {}, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    let httpParams = new HttpParams();

    for (const key of Object.keys(bodyData)) {
      // @ts-expect-error foo
      httpParams = httpParams.append(key, bodyData[key]);
    }

    const token = this.auth.token;
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    return this.http
      .put<BaseResponse<T>>(
        this._baseUrl + verb,
        {},
        {
          headers: headers,
          params: httpParams
        }
      )
      .pipe(
        catchError(this.handleError),
        map((data) => data.response)
      );
  }

  public get<T>(endpoint: string, bodyData: object = {}, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    let httpParams = new HttpParams();

    for (const key of Object.keys(bodyData)) {
      // @ts-expect-error foo
      httpParams = httpParams.append(key, bodyData[key]);
    }

    const token = this.auth.token;
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    return this.http
      .get<BaseResponse<T>>(this._baseUrl + endpoint, {
        headers: headers,
        params: httpParams
      })
      .pipe(
        catchError(this.handleError),
        map((data) => data.response)
      );
  }

  public delete<T>(verb?: string, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    const token = this.auth.token;
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    return this.http
      .delete<BaseResponse<T>>(this._baseUrl + verb, {
        headers: headers
      })
      .pipe(
        catchError(this.handleError),
        map((data) => data.response)
      );
  }

  public getBlob(
    endpoint: string,
    bodyData: object = {},
    headers: HttpHeaders = new HttpHeaders()
  ): Observable<HttpResponse<ArrayBuffer>> {
    let httpParams = new HttpParams();

    for (const key of Object.keys(bodyData)) {
      // @ts-expect-error foo
      httpParams = httpParams.append(key, bodyData[key]);
    }

    const token = this.auth.token;
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    return this.http
      .get(this._baseUrl + endpoint, {
        headers: headers,
        params: httpParams,
        observe: 'response',
        responseType: 'arraybuffer'
      })
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (isDevMode()) {
      if (error.error instanceof ErrorEvent) {
        console.error('An error occurred:', error.error.message);
      } else {
        console.error(`Backend returned code ${error.status}`, error.error.text);
      }
    }

    return throwError(error);
  }
}
